/**
 * Live Trade Insights — Mid-Trade Coaching Signals
 *
 * While a trade is live (`trade.isOpen === true`), surface actionable signals
 * drawn from the trader's own history. Different from the Coach Engine's
 * Observation system (which is post-hoc: day/session/progress rollups):
 *   - Runs continuously on each open trade
 *   - Produces compact, glance-able chips rendered inside the trade card
 *   - Draws on the SAME data sources (allTrades, journalEntry.eventTags, etc.)
 *     so free/pro gating + the trader's own patterns stay consistent
 *
 * Philosophy: "the coach whispers while you're in the trade — it doesn't
 * shout a post-mortem after you exit." High-signal only. We cap signals and
 * silence when evidence is thin.
 *
 * ── Signal types ─────────────────────────────────────────────────────────
 * 1. Setup history    — WR / avg P&L for this setup (≥3 prior)             [Free]
 * 2. Hour-window      — WR for this hour block (≥5 prior, ≥15pp delta)     [Free]
 * 3. Overtrading      — 3+ trades in the last 15min on same account        [Free]
 * 4. Upcoming event   — high/med event <30m away + historical pattern      [Pro]
 * 5. Today-tagged     — today's journal has an event tag + history pattern [Pro]
 *                       (suppressed when #4 fires — same info, less urgent)
 *
 * Signal shape:
 *   { key, tone: 'info'|'warn'|'success', tier: 'free'|'pro',
 *     label, detail, priority }
 *
 * Consumer sorts by priority desc and caps at MAX_SIGNALS to avoid flooding
 * the panel when everything has a story.
 */

import { parseHM, withNetPnl, summarizePnl } from './utils.js';
import { signalSignature, signalLabel, normalizeDirection } from '../barAnalysis/counterSignals.js';
import { buildRealtimeCounterSignals } from './realtimeCounterSignals.js';

// Cap so the live panel never stacks more than a short, scannable list.
// Rank+cap rather than hide any single domain so the user sees the top thing
// from whichever bucket matters most this moment.
const MAX_SIGNALS = 3;

// Overtrading window: anything > 2 opens in this window on the same account
// is a tilt signal. Matches dayOvertrading's CLUSTER_WINDOW_MIN default.
const OVERTRADING_WINDOW_MIN = 15;
const OVERTRADING_THRESHOLD  = 3;

// Hour-window threshold for meaningful "you struggle in this hour" vs
// "you excel in this hour" — anything smaller is noise.
const HOUR_WR_DELTA_PCT = 15;

// Upcoming-event lookahead window. A 30-minute horizon matches the
// DEFAULT_PROXIMITY_MIN in eventAutoTag so the whisper and the eventual
// auto-tag apply to the same pre-event period.
const UPCOMING_EVENT_WINDOW_MIN = 30;

// Minimum prior samples before we emit a given signal. We intentionally skew
// higher than the post-hoc analyzers because live signals interrupt focus — we
// need to be more confident before we distract the trader.
const MIN_SAMPLE_SETUP   = 3;
const MIN_SAMPLE_HOUR    = 5;
const MIN_SAMPLE_EVENT   = 3;

// Duration-profile minimum sample. Need ≥4 winners + ≥4 losers on this setup
// to compute meaningful medians; below that the median is one trade's duration
// and the "past your typical winner" framing would be silly.
const MIN_SAMPLE_DURATION = 4;
// How far PAST the winner median triggers the "you're late" signal. 1.0 ×
// the median means as soon as the trade crosses the median; 1.25 gives some
// breathing room so we don't fire the moment the trade equals median.
const DURATION_PAST_WINNER_MULT = 1.25;

const fmtUSD = (n) => `${n >= 0 ? '+' : '−'}$${Math.abs(n).toFixed(0)}`;

// Bucket an hour number (0-23) into a readable label. Keep in sync with
// humanHour in dayUtils — duplicated here to avoid the Coach dayUtils dep
// since this module is consumed by the renderer layer too.
const hourLabel = (h) => {
  if (h === null || h === undefined) return '';
  const period = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}${period}`;
};

/**
 * Given the open trade's entry time, return the hour (0-23) or null.
 */
const entryHour = (trade) => {
  const mins = parseHM(trade?.entryTime);
  return mins === null ? null : Math.floor(mins / 60);
};

/**
 * Aggregate net P&L + WR for a subset of trades.
 */
const aggregate = (enriched) => {
  if (enriched.length === 0) return null;
  const { wins, total, winRate } = summarizePnl(enriched);
  return {
    sample: enriched.length,
    wins,
    avgPnl: total / enriched.length,
    totalPnl: total,
    winRatePct: winRate * 100,
  };
};

// ── Signal builders ─────────────────────────────────────────────────────────

/**
 * Signal 1 — Setup history. Fires when the open trade has a setup tagged and
 * the trader has ≥MIN_SAMPLE_SETUP prior closed trades of that setup.
 *
 * We deliberately skip the "lukewarm" middle ground (neither clearly negative
 * nor clearly positive). A chip that whispers "50% WR over 3 trades, +$10
 * avg" is low-confidence noise — it teaches the user to ignore chips. Silence
 * beats a shrug here: the user only gets a setup chip when the history
 * actually has something to say.
 */
const buildSetupSignal = (trade, enrichedPast) => {
  const setup = trade?.setup;
  if (!setup) return null;

  // Direction-match guard (v7.6.x). Setup names like "Support/Resistance"
  // or "VWAP Bounce" are direction-flexible — the trader takes them long
  // at support / short at resistance. The setup's WR is fundamentally
  // direction-segregated: a 60% long WR + 40% short WR averaging to 50%
  // is meaningless guidance because the user is in ONE direction right
  // now. Filter the cohort to past trades matching the current trade's
  // direction so the WR signal applies. For direction-implicit setups
  // (e.g., "Wolf Stack" is always long by definition) every past trade
  // matches anyway — the filter is a benign no-op.
  const currentDirection = normalizeDirection(trade?.direction);
  if (!currentDirection) return null;

  // Case/whitespace-insensitive match: traders type "MMXM", "mmxm", "MMXM "
  // across days; treating those as different setups silently shrinks every
  // setup history bucket below the MIN_SAMPLE_SETUP threshold for no reason.
  const setupKey = String(setup).trim().toLowerCase();
  const matching = enrichedPast.filter(({ trade: t }) => {
    if (!t?.setup) return false;
    if (String(t.setup).trim().toLowerCase() !== setupKey) return false;
    return normalizeDirection(t.direction) === currentDirection;
  });
  if (matching.length < MIN_SAMPLE_SETUP) return null;

  const agg = aggregate(matching);
  if (!agg) return null;

  const isNeg = agg.avgPnl < -20;
  const isPos = agg.avgPnl > 20 && agg.winRatePct >= 55;
  if (!isNeg && !isPos) return null;  // mute lukewarm signals

  return {
    key: 'setup-history',
    tone: isNeg ? 'warn' : 'success',
    tier: 'free',
    label: `${setup}: ${agg.winRatePct.toFixed(0)}% WR · ${agg.sample} trades`,
    detail: `${fmtUSD(agg.avgPnl)} avg P&L on prior ${setup} trades.`,
    priority: isNeg ? 78 : 55,
  };
};

/**
 * Signal 2 — Hour-window WR. Fires when the current hour bucket has ≥5 prior
 * trades AND its WR differs from overall WR by ≥15 pct pts.
 */
const buildHourSignal = (trade, enrichedPast) => {
  const h = entryHour(trade);
  if (h === null) return null;

  // Direction-match guard (v7.6.x). Hour-of-day stats are direction-
  // sensitive: a bullish 9:30am open means longs win at 70% / shorts at
  // 30%, but blended that's 50% and tells the trader nothing useful. The
  // baseline `allAgg` is also computed from the SAME-direction past
  // pool so the +/- delta stays meaningful — comparing this-hour same-
  // direction stats against blended-direction overall would mix
  // direction effects into the hour signal and reintroduce the bug.
  const currentDirection = normalizeDirection(trade?.direction);
  if (!currentDirection) return null;
  const sameDirectionPast = enrichedPast.filter(({ trade: t }) =>
    normalizeDirection(t.direction) === currentDirection
  );

  const matching = sameDirectionPast.filter(({ trade: t }) => {
    // Skip trades with no parseable entryTime — they can't match the
    // current hour. The previous `?? -999` sentinel worked but was opaque
    // and would coincidentally match if `h` ever happened to equal -17.
    const m = parseHM(t.entryTime);
    return m !== null && Math.floor(m / 60) === h;
  });
  if (matching.length < MIN_SAMPLE_HOUR) return null;

  const hourAgg = aggregate(matching);
  const allAgg = aggregate(sameDirectionPast);
  if (!hourAgg || !allAgg) return null;

  const delta = hourAgg.winRatePct - allAgg.winRatePct;
  if (Math.abs(delta) < HOUR_WR_DELTA_PCT) return null;

  const isNeg = delta < 0;
  return {
    key: 'hour-window',
    tone: isNeg ? 'warn' : 'success',
    tier: 'free',
    label: `${hourLabel(h)} block: ${hourAgg.winRatePct.toFixed(0)}% WR (${delta > 0 ? '+' : ''}${delta.toFixed(0)}pp)`,
    detail: isNeg
      ? `Historically weaker than your ${allAgg.winRatePct.toFixed(0)}% overall — tighter criteria helps.`
      : `Stronger than your ${allAgg.winRatePct.toFixed(0)}% overall — this is a time window where you excel.`,
    priority: isNeg ? 72 : 50,
  };
};

/**
 * Signal 3 — Overtrading forming. Fires when the open trade is the 3rd+ on
 * the same account in the last 15 minutes (inclusive of itself).
 *
 * Safety: if the current trade has no accountId (e.g. a manual entry that
 * skipped the account field), we don't fire at all. The alternative —
 * grouping all undefined-account trades together — would false-positive
 * across unrelated accounts whenever multiple trades lack account data.
 */
const buildOvertradingSignal = (trade, todayTrades) => {
  if (!Array.isArray(todayTrades) || todayTrades.length < OVERTRADING_THRESHOLD - 1) return null;
  if (!trade?.accountId) return null;  // avoid grouping undefined-account trades
  const selfMins = parseHM(trade?.entryTime);
  if (selfMins === null) return null;

  const sameAcct = todayTrades.filter(t =>
    t.id !== trade.id &&
    t.accountId &&
    t.accountId === trade.accountId
  );
  const recent = sameAcct.filter(t => {
    const m = parseHM(t.entryTime);
    return m !== null && (selfMins - m) >= 0 && (selfMins - m) <= OVERTRADING_WINDOW_MIN;
  });

  // +1 to include the current open trade itself
  const count = recent.length + 1;
  if (count < OVERTRADING_THRESHOLD) return null;

  return {
    key: 'overtrading-forming',
    tone: 'warn',
    tier: 'free',
    label: `${count} trades in ${OVERTRADING_WINDOW_MIN}min on this account`,
    detail: 'Rapid re-entry is usually tilt. Breath test before you add.',
    priority: 88,
  };
};

/**
 * Compute the duration of a closed trade in minutes. Uses the LAST exit's
 * time when present, falling back to trade.exitTime. Returns null when
 * either timestamp is unparseable or duration is non-positive.
 *
 * Why last-exit-time over exitTime: trades can have a stale exitTime that
 * doesn't match the actual last exit (e.g., user edited times manually);
 * the exits[] array is the canonical record of what happened.
 */
const tradeDurationMin = (trade) => {
  if (!trade?.entryTime) return null;
  const entryMin = parseHM(trade.entryTime);
  if (entryMin === null) return null;
  const exits = Array.isArray(trade.exits) ? trade.exits : [];
  let exitTimeStr = null;
  for (let i = exits.length - 1; i >= 0; i--) {
    if (exits[i]?.time) { exitTimeStr = exits[i].time; break; }
  }
  if (!exitTimeStr) exitTimeStr = trade.exitTime || null;
  if (!exitTimeStr) return null;
  const exitMin = parseHM(exitTimeStr);
  if (exitMin === null) return null;
  // Same-day duration; if exit < entry treat as overnight (rare for futures
  // intraday, but common for held-overnight prop accounts) by adding 24h
  // worth of minutes. We cap at +24h to avoid absurd durations from clock
  // skew or bad data.
  let dur = exitMin - entryMin;
  if (dur < 0) dur += 24 * 60;
  if (dur <= 0 || dur > 24 * 60) return null;
  return dur;
};

/**
 * Median of a numeric array (no mutation). Returns null on empty.
 */
const median = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const n = sorted.length;
  const mid = n >> 1;
  return n % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

/**
 * Live elapsed minutes for the open trade — best-effort from entryTime + the
 * caller-supplied current minute-of-day. Returns null when entry can't parse.
 *
 * Why we accept nowMinutes from outside: LiveTradePanel already maintains
 * a 15s-tick clock that recomputes "now" in ET; reusing it keeps the live
 * signal in sync with the elapsed-timer in the panel header.
 */
const liveElapsedMin = (trade, nowMinutes) => {
  if (!Number.isFinite(nowMinutes)) return null;
  const entryMin = parseHM(trade?.entryTime);
  if (entryMin === null) return null;
  let elapsed = nowMinutes - entryMin;
  // Same overnight-tolerant adjustment as tradeDurationMin
  if (elapsed < 0) elapsed += 24 * 60;
  if (elapsed < 0 || elapsed > 24 * 60) return null;
  return elapsed;
};

/**
 * Signal: Duration profile.
 *
 * Compares time-in-trade against the median winner + median loser duration
 * for the same setup in trader history. Two surfaces:
 *
 *   - Past winner median (warn): you've crossed where most winners exited.
 *     Either trim/run conviction or recognize the trade has run out of
 *     winner runway.
 *   - Past loser median, still in (info): most of your losers stop out
 *     before now; if you're still in, you've cleared the typical failure
 *     window. Confidence-builder for holding.
 *
 * Both surface only when each side has ≥ MIN_SAMPLE_DURATION trades, which
 * keeps the medians from being one-trade-driven noise.
 */
const buildDurationSignal = (trade, enrichedPast, nowMinutes) => {
  const setup = trade?.setup;
  if (!setup) return null;
  const elapsed = liveElapsedMin(trade, nowMinutes);
  if (elapsed === null) return null;

  // Direction-match guard (v7.6.x). Duration profiles differ meaningfully
  // by direction even on the same setup — a "VWAP Bounce" long held to a
  // 12-minute median runner profile may behave very differently as a
  // short against an uptrend (quicker stop-outs, shorter losers). Filter
  // the cohort so winner/loser medians come from same-direction history.
  const currentDirection = normalizeDirection(trade?.direction);
  if (!currentDirection) return null;

  // Same case-insensitive setup match as the setup-history signal.
  const setupKey = String(setup).trim().toLowerCase();
  const matching = enrichedPast.filter(({ trade: t }) => {
    if (!t?.setup) return false;
    if (String(t.setup).trim().toLowerCase() !== setupKey) return false;
    return normalizeDirection(t.direction) === currentDirection;
  });
  if (matching.length === 0) return null;

  const winners = [];
  const losers = [];
  for (const { trade: t, netPnl } of matching) {
    const d = tradeDurationMin(t);
    if (d === null) continue;
    if (Number.isFinite(netPnl) && netPnl > 0) winners.push(d);
    else if (Number.isFinite(netPnl)) losers.push(d);
  }
  if (winners.length < MIN_SAMPLE_DURATION || losers.length < MIN_SAMPLE_DURATION) return null;

  const winnerMedian = median(winners);
  const loserMedian  = median(losers);

  // Past winner median by the multiplier — "the clock is no longer with you"
  if (winnerMedian != null && elapsed >= winnerMedian * DURATION_PAST_WINNER_MULT) {
    return {
      key: 'duration-past-winner',
      tone: 'warn',
      tier: 'free',
      label: `Past your typical winner duration on ${setup} (${Math.round(winnerMedian)}m median)`,
      detail: `You're ${Math.round(elapsed)}m in. Most ${setup} winners exit by ${Math.round(winnerMedian)}m. Trim, runner-trail, or recognize the runway is gone.`,
      priority: 70,
    };
  }

  // Past loser median, still in — confidence-builder
  if (loserMedian != null && elapsed >= loserMedian && (winnerMedian == null || elapsed < winnerMedian)) {
    return {
      key: 'duration-cleared-loser',
      tone: 'success',
      tier: 'free',
      label: `Cleared typical loser window on ${setup} (${Math.round(loserMedian)}m median)`,
      detail: `Most ${setup} losers stop out by ${Math.round(loserMedian)}m. You're ${Math.round(elapsed)}m in and still working — historically a good sign.`,
      priority: 50,
    };
  }
  return null;
};

/**
 * Fuzzy-match an event name against a historical tag. Exact (case-insensitive)
 * match first, then substring either direction so "CPI" matches "CPI Report"
 * and vice versa. A strict equality-only check misses real overlap because the
 * economic-calendar API names events with suffixes ("CPI Report", "FOMC
 * Meeting") while users tend to tag short ("CPI", "FOMC"). The two-way
 * substring fallback intentionally over-matches (e.g. "Core CPI" matches
 * "CPI") — in practice that's the right call for history: Core CPI history is
 * relevant context for a CPI open.
 */
const eventNameMatches = (eventName, tag) => {
  const e = String(eventName || '').toLowerCase().trim();
  const t = String(tag || '').toLowerCase().trim();
  if (!e || !t) return false;
  if (e === t) return true;
  // Substring match is fine for real event names ("CPI Report" ↔ "CPI") but
  // falls apart on tiny tags: a 2-letter tag like "FX" or "M" matches almost
  // anything via `includes`. Require both sides to be ≥3 chars before allowing
  // substring matching; below that, only exact-equal counts.
  if (e.length < 3 || t.length < 3) return false;
  return e.includes(t) || t.includes(e);
};

/**
 * Aggregate day-level P&L for trades restricted to a set of past dates, then
 * return `{ avgDaily, negDays, dailyPnls }`. Extracted because both the
 * upcoming-event signal and the today-tagged event signal need the same
 * day-level rollup (a per-trade aggregate would overweight days with many
 * trades — 1 big day shouldn't swing the pattern harder than 1 flat day).
 */
const computeEventDayPattern = (pastTrades, getTradeNetPnL, getAccountById) => {
  if (!Array.isArray(pastTrades) || pastTrades.length === 0) return null;
  const enriched = withNetPnl(pastTrades, getTradeNetPnL, getAccountById);
  const byDate = {};
  for (const { trade, netPnl } of enriched) {
    if (!byDate[trade.date]) byDate[trade.date] = 0;
    byDate[trade.date] += netPnl;
  }
  const dailyPnls = Object.values(byDate);
  if (dailyPnls.length === 0) return null;
  const avgDaily = dailyPnls.reduce((s, p) => s + p, 0) / dailyPnls.length;
  const negDays = dailyPnls.filter(p => p < 0).length;
  return { avgDaily, negDays, dailyPnls };
};

/**
 * Counter-Signal whisper (Phase 19).
 *
 * Live-trade variant of the inTradeCounterSignal analyzer. The post-hoc
 * analyzer needs the trade's barAnalysis.counterSignals (populated at
 * backfill); the LIVE version can't wait for backfill to run on an
 * in-progress trade — so instead, this whisper looks at the trader's
 * HISTORY for the current setup and asks:
 *
 *   "On this setup, when {signal} fires, your WR drops dramatically.
 *    Stay alert — if you see a counter-candle close, act on it."
 *
 * The live whisper is a PRIMING signal — it tells the trader what to
 * watch for. The actual real-time "the signal just fired" detection
 * requires live bars piped to the renderer, which isn't wired yet
 * (the bar files only refresh on close, and the renderer reads them
 * via the journifiBars CSV source on demand, not via subscription).
 *
 * MIN_SAMPLE_COUNTER_SIGNAL of 3 is intentionally low because counter-
 * signals fire on only a fraction of trades — even 3 prior cases of a
 * specific signature is meaningful pattern data.
 */
const MIN_SAMPLE_COUNTER_SIGNAL = 3;
const MIN_WR_GAP_COUNTER_SIGNAL = 0.20;

const buildCounterSignalSignal = (trade, enrichedPast) => {
  const setup = trade?.setup;
  if (!setup) return null;
  const setupKey = String(setup).trim().toLowerCase();

  // Direction-match guard (v7.6.x). Counter-signals are direction-aware
  // events: for a LONG trade the adverse level-break is `entry-bar-low`
  // (price slips below your entry bar's low). For a SHORT, the adverse
  // level-break is `entry-bar-high`. Same applies to EMA and VWAP
  // close-against — a 9 EMA close BELOW is adverse for longs, ABOVE for
  // shorts.
  //
  // Without this filter, a LONG trade on "Support/Resistance" could be
  // primed with "Watch 15m broke entry-bar-high" — a signal that's only
  // adverse for shorts, and is actually BULLISH-CONFIRMING for the
  // current long. The cohort pulled stats from past SHORT trades on the
  // same setup, then surfaced them to the wrong direction. Pure noise.
  //
  // The fix is universal across all signal types: cohort only with past
  // trades whose direction matches the current trade's direction, so the
  // signatures that survive are by construction relevant to this trade.
  const currentDirection = normalizeDirection(trade?.direction);
  if (!currentDirection) return null;

  // Filter to prior CLOSED trades on this setup, in the same direction,
  // that have barAnalysis (otherwise we don't know if a signal fired).
  const relevant = enrichedPast.filter(({ trade: t }) => {
    if (!t?.setup) return false;
    if (String(t.setup).trim().toLowerCase() !== setupKey) return false;
    if (normalizeDirection(t.direction) !== currentDirection) return false;
    const events = t.barAnalysis?.counterSignals;
    return Array.isArray(events);  // empty array is fine — means "scanned, none"
  });
  if (relevant.length < MIN_SAMPLE_COUNTER_SIGNAL) return null;

  // Build per-signature cohorts: fired vs didn't fire.
  // `sigData` :: Map<signature, { firedWins, firedTotal, baseWins, baseTotal, label }>
  const sigData = new Map();
  for (const { trade: t, netPnl } of relevant) {
    const events = t.barAnalysis.counterSignals || [];
    const win = Number.isFinite(netPnl) && netPnl > 0;
    const uniqHere = new Map();
    for (const ev of events) {
      const sig = signalSignature(ev);
      if (!sig) continue;
      if (!uniqHere.has(sig)) uniqHere.set(sig, ev);
    }
    // For EVERY known signature seen across all trades, this trade either fired or didn't.
    // We seed signatures as we discover them so the baseline cohort is accurate.
    for (const [sig, ev] of uniqHere.entries()) {
      if (!sigData.has(sig)) {
        sigData.set(sig, { firedWins: 0, firedTotal: 0, baseWins: 0, baseTotal: 0, label: signalLabel(ev) });
      }
      const slot = sigData.get(sig);
      slot.firedTotal++;
      if (win) slot.firedWins++;
    }
  }
  // Second pass: for each trade, every signature it DIDN'T have fires the baseline counter.
  for (const { trade: t, netPnl } of relevant) {
    const events = t.barAnalysis.counterSignals || [];
    const win = Number.isFinite(netPnl) && netPnl > 0;
    const uniqHere = new Set(events.map(signalSignature).filter(Boolean));
    for (const [sig, slot] of sigData.entries()) {
      if (uniqHere.has(sig)) continue;
      slot.baseTotal++;
      if (win) slot.baseWins++;
    }
  }

  // Pick the signature with the biggest WR drop AND meeting sample mins.
  let best = null;
  let bestGap = 0;
  for (const [sig, slot] of sigData.entries()) {
    if (slot.firedTotal < MIN_SAMPLE_COUNTER_SIGNAL) continue;
    if (slot.baseTotal < MIN_SAMPLE_COUNTER_SIGNAL) continue;
    const firedWR = slot.firedWins / slot.firedTotal;
    const baseWR = slot.baseWins / slot.baseTotal;
    const gap = baseWR - firedWR;
    if (gap < MIN_WR_GAP_COUNTER_SIGNAL) continue;
    if (gap > bestGap) {
      best = { sig, slot, firedWR, baseWR, gap };
      bestGap = gap;
    }
  }
  if (!best) return null;

  const firedPct = Math.round(best.firedWR * 100);
  const basePct = Math.round(best.baseWR * 100);

  return {
    key: `counter-signal:${best.sig}`,
    tone: 'warn',
    tier: 'free',
    label: `Watch ${best.slot.label}`,
    detail: `When this fired on "${setup}", WR dropped from ${basePct}% → ${firedPct}% (${best.slot.firedTotal} trades). If you see a counter-candle close, act on it.`,
    priority: 78,
  };
};

/**
 * Find every past date that ought to count as an "${eventName} day". Looks at
 * two sources because the trader can land event evidence in either:
 *   1. `journalEntry.eventTags` — set when they click "Tag" in the Economic
 *      Events panel for that day.
 *   2. trade-level `tags` — set automatically by `autoTagTradesFromEvents`
 *      when a trade entry lands within the proximity window of a calendar
 *      event. Some traders never use the journal-level tag at all and rely
 *      entirely on the auto-tag — without source 2 we'd silently undercount
 *      their event history.
 *
 * Source 2 only adds dates not already covered by source 1; we don't need to
 * double-credit a day that's tagged both ways.
 */
const collectPastEventDates = (eventName, allJournalEntries, allTrades, todayDate = null) => {
  const dates = new Set();

  if (allJournalEntries) {
    for (const [d, entry] of Object.entries(allJournalEntries)) {
      if (todayDate && d === todayDate) continue;
      if (!Array.isArray(entry?.eventTags)) continue;
      if (entry.eventTags.some(tag => eventNameMatches(eventName, tag))) {
        dates.add(d);
      }
    }
  }

  if (Array.isArray(allTrades)) {
    for (const t of allTrades) {
      if (!t?.date) continue;
      if (todayDate && t.date === todayDate) continue;
      if (dates.has(t.date)) continue;  // already covered via journal-level tag
      if (!Array.isArray(t?.tags)) continue;
      if (t.tags.some(tag => eventNameMatches(eventName, tag))) {
        dates.add(t.date);
      }
    }
  }

  return [...dates];
};

/**
 * Collect all candidate event names that "apply to today". Same rationale as
 * `collectPastEventDates` — today-side evidence can sit on the journal entry
 * OR on individual trades. For the trade-level source we filter to tags that
 * look like event names (i.e., they fuzzy-match at least one historical
 * event-tag) so that mistake/confluence tags don't pollute the candidate pool.
 *
 * Returned candidates are de-duped case/whitespace-insensitively but preserve
 * the original casing of the first occurrence so the chip label looks right.
 */
const collectTodayEventCandidates = (journalEntry, todayTrades, allJournalEntries) => {
  const seen = new Map();  // normalizedKey → originalCasing
  const add = (raw) => {
    if (!raw) return;
    const s = String(raw).trim();
    if (!s) return;
    const k = s.toLowerCase();
    if (!seen.has(k)) seen.set(k, s);
  };

  const journalTags = Array.isArray(journalEntry?.eventTags) ? journalEntry.eventTags : [];
  journalTags.forEach(add);

  if (Array.isArray(todayTrades) && allJournalEntries) {
    // Build the set of "known event names" once so we can verify each trade
    // tag against history without an O(N×M) scan per trade.
    const knownEventNames = new Set();
    for (const entry of Object.values(allJournalEntries)) {
      if (!Array.isArray(entry?.eventTags)) continue;
      for (const t of entry.eventTags) {
        if (t) knownEventNames.add(String(t).trim());
      }
    }

    for (const trade of todayTrades) {
      if (!Array.isArray(trade?.tags)) continue;
      for (const tag of trade.tags) {
        if (!tag) continue;
        const tagStr = String(tag).trim();
        // Only treat trade-level tags as event candidates if some past
        // journal entry has tagged the same name. This is the "looks like
        // an event" gate that keeps mistake/confluence tags out.
        for (const known of knownEventNames) {
          if (eventNameMatches(tagStr, known)) {
            add(tagStr);
            break;
          }
        }
      }
    }
  }

  return [...seen.values()];
};

/**
 * Choose the candidate event tag with the deepest historical coverage. If two
 * tags tie, the first one wins (preserves source order — journal-level tags
 * naturally come first in `collectTodayEventCandidates`). Returns null if no
 * candidate clears `MIN_SAMPLE_EVENT`.
 *
 * Picking by coverage (vs. blindly taking `tags[0]`) matters when a day is
 * tagged with multiple events: we want to whisper using whichever event
 * actually has enough history to say something confident. A "GDP" tag with 8
 * past dates beats a "Beige Book" tag with 2.
 */
const pickBestEventTag = (candidates, allJournalEntries, allTrades, todayDate) => {
  let best = null;
  for (const tag of candidates) {
    const pastDates = collectPastEventDates(tag, allJournalEntries, allTrades, todayDate);
    if (pastDates.length < MIN_SAMPLE_EVENT) continue;
    if (!best || pastDates.length > best.pastDates.length) {
      best = { tag, pastDates };
    }
  }
  return best;
};

/**
 * Signal 4 — Upcoming event (Pro). Fires when today's economic calendar has a
 * high- or medium-importance event landing in the next UPCOMING_EVENT_WINDOW_MIN
 * minutes AND the trader has ≥3 prior tagged days for that event name.
 *
 * Philosophy: forward-looking coaching is the whole point of whispering during
 * a trade. "CPI in 18m and you're −$240 avg over 6 CPI days" is strictly more
 * actionable than a post-hoc "today was a CPI day" recap — it gives the trader
 * the option to tighten risk or wait for the print before the event fires.
 *
 * When this fires, `computeLiveInsights` suppresses signal 5 (today-tagged
 * event recap) because both would be showing the same history — we prefer the
 * time-anchored version.
 */
const buildUpcomingEventSignal = (
  nowMinutes,
  todayEvents,
  allJournalEntries,
  allTrades,
  getTradeNetPnL,
  getAccountById
) => {
  if (nowMinutes === null || nowMinutes === undefined) return null;
  if (!Array.isArray(todayEvents) || todayEvents.length === 0) return null;
  if (!allJournalEntries || !Array.isArray(allTrades) || allTrades.length === 0) return null;

  // Score each event: importance rank (high beats med), then nearest-first.
  // Low-importance events never fire a whisper — a dividend ex-date doesn't
  // deserve to interrupt a live trade.
  const impRank = (imp) => {
    const k = String(imp || '').toLowerCase();
    if (k === 'high') return 0;
    if (k === 'medium' || k === 'med') return 1;
    return 9;  // skipped by the filter below
  };

  const candidates = todayEvents
    .map(e => ({ ...e, mins: parseHM(e?.time), rank: impRank(e?.importance) }))
    .filter(e => e.rank < 9 && e.mins !== null)
    .filter(e => {
      const delta = e.mins - nowMinutes;
      return delta > 0 && delta <= UPCOMING_EVENT_WINDOW_MIN;
    })
    .sort((a, b) => (a.rank - b.rank) || (a.mins - b.mins));

  if (candidates.length === 0) return null;

  const event = candidates[0];
  const minsAway = event.mins - nowMinutes;

  // Reverse-index past dates whose eventTags (journal-level OR trade-level)
  // fuzzy-match this event's name. See `collectPastEventDates` for the full
  // rationale on the two-source union.
  const pastDates = collectPastEventDates(event.name, allJournalEntries, allTrades);

  if (pastDates.length < MIN_SAMPLE_EVENT) return null;

  const pastTrades = allTrades.filter(t => pastDates.includes(t.date));
  const pattern = computeEventDayPattern(pastTrades, getTradeNetPnL, getAccountById);
  if (!pattern) return null;
  if (pattern.dailyPnls.length < MIN_SAMPLE_EVENT) return null;

  const { avgDaily, negDays, dailyPnls } = pattern;
  const isNeg = avgDaily < -50;
  const isPos = avgDaily > 50;
  if (!isNeg && !isPos) return null;

  return {
    key: `upcoming-event:${event.name}`,
    tone: isNeg ? 'warn' : 'success',
    tier: 'pro',
    label: `${event.name} in ${minsAway}m · ${fmtUSD(avgDaily)} avg · ${dailyPnls.length} prior`,
    detail: isNeg
      ? `${negDays}/${dailyPnls.length} prior ${event.name} days were red. Tighten risk or wait for the print.`
      : `You've had an edge on prior ${event.name} days. Keep the playbook tight.`,
    priority: isNeg ? 85 : 58,
  };
};

/**
 * Signal 5 — Today-tagged event recap (Pro). Fires when today has at least one
 * event tag (journal-level OR auto-tagged onto a trade) AND the strongest tag
 * has ≥MIN_SAMPLE_EVENT prior days of history.
 *
 * "Strongest tag" = the candidate with the most past-date coverage. This beats
 * blindly grabbing `eventTags[0]` because a multi-tagged day (e.g. "CPI" +
 * "Beige Book") shouldn't lose its CPI history just because Beige Book was
 * typed first.
 *
 * Suppressed by computeLiveInsights when buildUpcomingEventSignal fires for
 * the same event type — the upcoming version is more actionable.
 */
const buildEventSignal = (journalEntry, todayTrades, allJournalEntries, allTrades, getTradeNetPnL, getAccountById) => {
  if (!allJournalEntries || !Array.isArray(allTrades) || allTrades.length === 0) return null;

  const todayDate = journalEntry?.date;
  const candidates = collectTodayEventCandidates(journalEntry, todayTrades, allJournalEntries);
  if (candidates.length === 0) return null;

  const best = pickBestEventTag(candidates, allJournalEntries, allTrades, todayDate);
  if (!best) return null;

  const { tag, pastDates } = best;
  const pastTrades = allTrades.filter(t => pastDates.includes(t.date));
  const pattern = computeEventDayPattern(pastTrades, getTradeNetPnL, getAccountById);
  if (!pattern) return null;
  if (pattern.dailyPnls.length < MIN_SAMPLE_EVENT) return null;

  const { avgDaily, negDays, dailyPnls } = pattern;
  const isNeg = avgDaily < -50;
  const isPos = avgDaily > 50;
  if (!isNeg && !isPos) return null;

  return {
    key: `event-preview:${tag}`,
    tone: isNeg ? 'warn' : 'success',
    tier: 'pro',
    label: `${tag}: ${fmtUSD(avgDaily)} avg · ${dailyPnls.length} prior days`,
    detail: isNeg
      ? `${negDays}/${dailyPnls.length} ${tag} days were red. Size conservatively today.`
      : `Your edge has held on ${tag} days. Stick to the playbook.`,
    priority: isNeg ? 82 : 52,
    // Surfaced so computeLiveInsights can dedup against a concurrent upcoming-event signal
    _eventName: tag,
  };
};

// ── Public entry point ──────────────────────────────────────────────────────

/**
 * Compute live-trade signals for an open trade.
 *
 * @param {Object}   ctx
 * @param {Object}   ctx.trade              The open trade being evaluated
 * @param {Array}    ctx.allTrades          All closed+open trades across all days
 * @param {Array}    ctx.todayTrades        Trades on today's date (all accounts)
 * @param {Array}    ctx.todayEvents        Today's economic calendar events
 *                                          (shape: { name, time, importance }). Used by the
 *                                          upcoming-event signal to count down the next
 *                                          high/medium event.
 * @param {number}   ctx.nowMinutes         Current time expressed as minutes from local
 *                                          midnight. Caller buckets this to 1-minute
 *                                          precision to avoid churning the memo.
 * @param {Object}   ctx.journalEntry       Today's journal entry — expects { eventTags, date }
 * @param {Object}   ctx.allJournalEntries  Map of date → entry (for event history lookup)
 * @param {Function} ctx.getTradeNetPnL     (trade, account) → { netPnl }
 * @param {Function} ctx.getAccountById     (accountId) → account|null
 * @param {String}   ctx.userTier           'free' | 'pro' — filters out pro-only signals when free
 * @returns {Array}                         Ranked, capped list of signals
 */
export function computeLiveInsights(ctx) {
  const {
    trade,
    allTrades = [],
    todayTrades = [],
    todayEvents = [],
    nowMinutes = null,
    journalEntry = null,
    allJournalEntries = {},
    getTradeNetPnL,
    getAccountById,
    userTier = 'free',
    // Phase 19 — live bars for real-time counter-signal detection.
    // Shape: { '5m': bars[], '15m': bars[] }. Optional — caller (LiveTradePanel)
    // polls barDataService periodically and passes the latest cache.
    // When absent, real-time detection silently skips and only the
    // history-based priming whisper fires.
    htfBars = null,
  } = ctx || {};

  if (!trade || !trade.isOpen) return [];

  // Past = closed trades (any date ≤ today), excluding this trade itself.
  // Open trades don't yet have outcomes, so we exclude them from history.
  const past = allTrades.filter(t =>
    t &&
    t.id !== trade.id &&
    t.isOpen !== true &&
    // Only include trades with a parseable entry time and a P&L signal
    t.entryTime &&
    (typeof t.pnl !== 'undefined' || typeof t.netPnl !== 'undefined')
  );

  const enrichedPast = withNetPnl(past, getTradeNetPnL, getAccountById);

  const signals = [];

  const setupSignal = buildSetupSignal(trade, enrichedPast);
  if (setupSignal) signals.push(setupSignal);

  const hourSignal = buildHourSignal(trade, enrichedPast);
  if (hourSignal) signals.push(hourSignal);

  const overtradingSignal = buildOvertradingSignal(trade, todayTrades);
  if (overtradingSignal) signals.push(overtradingSignal);

  // Duration-profile signal (Phase 13) — compares time-in-trade to median
  // winner/loser duration on this setup. Free tier; high-signal because
  // it's actionable (trim/run decision) and only fires when sample is real.
  const durationSignal = buildDurationSignal(trade, enrichedPast, nowMinutes);
  if (durationSignal) signals.push(durationSignal);

  // Counter-signal whisper (Phase 19) — priming signal that warns the
  // trader to watch for the historically-predictive counter-signal on
  // this setup. Reads barAnalysis.counterSignals from prior closed
  // trades. Free tier.
  const counterSignal = buildCounterSignalSignal(trade, enrichedPast);
  if (counterSignal) signals.push(counterSignal);

  // Real-time counter-signal detection (Phase 19.2) — when LiveTradePanel
  // supplies htfBars (5m/15m), run the detector NOW to surface signals
  // that have already fired during the open trade. Higher priority than
  // the priming whisper since a real fire is more actionable than a
  // prediction. Dedup vs priming happens implicitly: keys are different
  // ('rt-counter:' prefix vs 'counter-signal:') so both can render, but
  // priority sorting puts the real-time one first.
  if (htfBars) {
    const rtSignals = buildRealtimeCounterSignals(trade, htfBars);
    if (rtSignals.length > 0) signals.push(...rtSignals);
  }

  if (userTier === 'pro') {
    // Upcoming first — it's more actionable (time-anchored, pre-event).
    const upcomingEvent = buildUpcomingEventSignal(
      nowMinutes, todayEvents, allJournalEntries, allTrades, getTradeNetPnL, getAccountById
    );
    if (upcomingEvent) signals.push(upcomingEvent);

    // Today-tagged recap — suppressed when the upcoming signal already covers
    // the same event name (fuzzy match). Showing both would double-chip the
    // same history; the upcoming version wins because it comes with a
    // countdown the trader can act on.
    const tagEvent = buildEventSignal(journalEntry, todayTrades, allJournalEntries, allTrades, getTradeNetPnL, getAccountById);
    if (tagEvent) {
      const isDup = upcomingEvent && eventNameMatches(
        upcomingEvent.key.replace(/^upcoming-event:/, ''),
        tagEvent._eventName || ''
      );
      if (!isDup) signals.push(tagEvent);
    }
  }

  // Rank by priority desc, cap
  signals.sort((a, b) => b.priority - a.priority);
  return signals.slice(0, MAX_SIGNALS);
}

// Small consumer-friendly exports — handy for tests + future extenders
export const LIVE_INSIGHT_LIMITS = {
  MAX_SIGNALS,
  OVERTRADING_WINDOW_MIN,
  OVERTRADING_THRESHOLD,
  HOUR_WR_DELTA_PCT,
  UPCOMING_EVENT_WINDOW_MIN,
  MIN_SAMPLE_SETUP,
  MIN_SAMPLE_HOUR,
  MIN_SAMPLE_EVENT,
  MIN_SAMPLE_DURATION,
  DURATION_PAST_WINNER_MULT,
};

// Test-friendly exports for the duration-profile signal builders.
export {
  buildDurationSignal,
  tradeDurationMin,
  liveElapsedMin,
  median as _durationMedian,
  // v7.6.x — exported so the direction-match regression suite can verify
  // that a long trade isn't primed with same-setup / same-hour / same-
  // signature data sourced from past shorts.
  buildCounterSignalSignal,
  buildSetupSignal,
  buildHourSignal,
};
